import React, { useState } from 'react';
import { Mail, Lock, LogIn, AlertCircle, Key, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import MasterCoifaLogo from '../components/layout/MasterCoifaLogo';
import api from '../utils/api';

interface LoginViewProps {
  onLoginSuccess: (user: { name: string; role: string; email: string; permissions?: any }, token?: string) => void;
}

type ViewMode = 'LOGIN' | 'REQUEST_CODE' | 'RESET_PASSWORD';

export default function LoginView({ onLoginSuccess }: LoginViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('LOGIN');
  const [emailOrUser, setEmailOrUser] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [code, setCode] = useState<string>('');
  
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // 1. Submit normal login (Email/User + Password)
  const handleNormalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOrUser || !password) {
      setErrorMsg('Por favor, preencha o e-mail/usuário e a senha.');
      return;
    }
    
    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const response = await api.post('/login', {
        email: emailOrUser,
        password: password
      });

      const userObj = response.data?.user || response.data?.data?.user;
      const token = response.data?.token || response.data?.data?.token;

      if (userObj) {
        onLoginSuccess({
          name: userObj.name || userObj.fullName || emailOrUser,
          role: userObj.role || 'Administração',
          email: userObj.email || emailOrUser,
          permissions: userObj.permissions
        }, token);
      } else if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE')) {
        setErrorMsg('O servidor retornou uma página HTML em vez da resposta da API. Verifique as rotas de backend.');
      } else {
        const fallbackMsg = response.data?.error || response.data?.message || 'Servidor retornou uma resposta inválida. Tente novamente.';
        setErrorMsg(fallbackMsg);
      }
    } catch (err: any) {
      console.error('Falha de login:', err);
      const errorStr = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Falha de comunicação com o servidor de autenticação.';
      setErrorMsg(errorStr);
    } finally {
      setLoading(false);
    }
  };

  // 2. Request OTP Code via email
  const handleRequestCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOrUser) {
      setErrorMsg('Por favor, digite seu e-mail ou nome de usuário cadastrado.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const response = await api.post('/request-code', {
        email: emailOrUser,
        username: emailOrUser
      });

      if (response.data && response.data.success) {
        // O código NÃO é capturado nem preenchido automaticamente por motivos de segurança (2FA via e-mail)
        setCode('');
        setSuccessMsg(response.data.message || 'Código de segurança enviado com sucesso para o seu e-mail cadastrado. Por favor, consulte sua caixa de entrada para digitar o código.');
        // Transiciona para a tela de redefinição aguardando a digitação manual do código pelo usuário
        setViewMode('RESET_PASSWORD');
      } else {
        const fallbackMsg = response.data?.error || response.data?.message || 'Erro inesperado ao processar solicitação.';
        setErrorMsg(fallbackMsg);
      }
    } catch (err: any) {
      console.error('Erro ao pedir código:', err);
      const errorStr = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Falha ao solicitar código. Verifique se o usuário está ativo.';
      setErrorMsg(errorStr);
    } finally {
      setLoading(false);
    }
  };

  // 3. Reset permanent password with recovery code
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOrUser || !code || !password || !confirmPassword) {
      setErrorMsg('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg('A nova senha e a confirmação digitadas não coincidem.');
      return;
    }

    if (password.length < 4) {
      setErrorMsg('A senha corporativa deve conter no mínimo 4 caracteres.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const response = await api.post('/reset-password', {
        email: emailOrUser,
        username: emailOrUser,
        code: code,
        password: password
      });

      if (response.data && response.data.success) {
        setSuccessMsg(response.data.message || 'Senha redefinida com sucesso!');
        setPassword('');
        setConfirmPassword('');
        setCode('');
        // Retorna para o login tradicional
        setViewMode('LOGIN');
      } else {
        const fallbackMsg = response.data?.error || response.data?.message || 'Não foi possível concluir a gravação da nova senha.';
        setErrorMsg(fallbackMsg);
      }
    } catch (err: any) {
      console.error('Falha ao redefinir senha:', err);
      const errorStr = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'O código de segurança informado é inválido ou expirou.';
      setErrorMsg(errorStr);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchMode = (mode: ViewMode) => {
    setViewMode(mode);
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  return (
    <div className="min-vh-100 w-100 d-flex flex-column flex-md-row bg-light text-dark" id="login-layout-wrapper">
      {/* Left Panel - Brand Display */}
      <div 
        className="col-12 col-md-5 bg-dark text-white d-flex flex-column justify-content-between p-5 position-relative overflow-hidden"
        style={{ 
          backgroundColor: '#032347', 
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundImage: 'linear-gradient(135deg, #021a36 0%, #063161 100%)'
        }}
        id="login-side-banner"
      >
        {/* Brand / Logo */}
        <div className="d-flex flex-column gap-3 align-items-start" id="login-brand-header">
          <div className="w-100 pb-3 border-bottom border-light border-opacity-10 text-start">
            <MasterCoifaLogo width="100%" height="90px" light={true} className="align-self-start" />
          </div>
          <div className="mt-2">
            <h1 className="fw-light text-uppercase tracking-wide font-display text-white opacity-90 m-0" style={{ fontSize: '2.5rem', lineHeight: '2.8rem' }}>
              CONTROLE DE <br/>
              <strong className="fw-bold">PRODUÇÃO</strong>
            </h1>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-auto d-flex flex-column gap-2" id="login-brand-footer">
          <p className="text-white-50 text-sm font-sans mb-3" style={{ maxWidth: '300px' }}>
            Sistema de Monitoramento de produção
          </p>
          <div className="d-flex align-items-center justify-content-between font-mono text-xs opacity-50 text-white pt-2 border-top border-light border-opacity-10">
            <span>Versão: V1.2.0</span>
            <span>&copy; {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Dynamic Interactive Auth Forms */}
      <div className="col-12 col-md-7 d-flex align-items-center justify-content-center p-4 bg-light" id="login-form-area">
        <div className="w-100" style={{ maxWidth: '420px' }}>
          
          {/* Header depending on ViewMode */}
          {viewMode === 'LOGIN' && (
            <div className="text-center mb-5">
              <h1 className="fw-light mb-1 text-dark tracking-tight font-display" style={{ fontSize: '2.2rem' }}>
                SEJA BEM-VINDO!
              </h1>
              <p className="text-muted text-sm font-sans">Faça login para iniciar as atividades</p>
            </div>
          )}

          {viewMode === 'REQUEST_CODE' && (
            <div className="mb-4">
              <button 
                onClick={() => handleSwitchMode('LOGIN')} 
                className="btn btn-link text-muted p-0 text-decoration-none d-flex align-items-center gap-1.5 mb-3 hover:text-dark font-sans text-xs"
              >
                <ArrowLeft size={14} /> Voltar para o Login
              </button>
              <h1 className="fw-light mb-1 text-dark tracking-tight font-display" style={{ fontSize: '1.8rem' }}>
                CÓDIGO DE ACESSO
              </h1>
              <p className="text-muted text-sm font-sans">Solicite seu código descartável de validação de e-mail</p>
            </div>
          )}

          {viewMode === 'RESET_PASSWORD' && (
            <div className="mb-4">
              <button 
                onClick={() => handleSwitchMode('LOGIN')} 
                className="btn btn-link text-muted p-0 text-decoration-none d-flex align-items-center gap-1.5 mb-3 hover:text-dark font-sans text-xs"
              >
                <ArrowLeft size={14} /> Cancelar e Voltar
              </button>
              <h1 className="fw-light mb-1 text-dark tracking-tight font-display" style={{ fontSize: '1.8rem' }}>
                DEFINIR SENHA
              </h1>
              <p className="text-muted text-sm font-sans">Crie sua nova senha corporativa segura</p>
            </div>
          )}

          {/* Feedback alerts */}
          {errorMsg && (
            <div className="alert alert-danger d-flex align-items-center gap-2 mb-4 text-xs py-2 px-3 rounded" role="alert">
              <AlertCircle size={16} />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="alert alert-success d-flex align-items-center gap-2 mb-4 text-xs py-2 px-3 rounded" role="alert">
              <CheckCircle size={16} className="text-success" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* ==================== VIEW 1: TRADITIONAL LOGIN ==================== */}
          {viewMode === 'LOGIN' && (
            <form onSubmit={handleNormalSubmit} className="d-flex flex-column gap-3.5">
              {/* Input Username/Email */}
              <div className="form-group position-relative">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0 text-muted px-3">
                    <Mail size={18} />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 py-2.5 px-2 text-dark font-sans shadow-none rounded-end"
                    placeholder="E-mail ou nome de usuário"
                    value={emailOrUser}
                    name="email"
                    onChange={(e) => {
                      setEmailOrUser(e.target.value);
                      setErrorMsg(null);
                    }}
                    style={{ backgroundColor: '#ffffff' }}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Input Password */}
              <div className="form-group position-relative mt-3">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0 text-muted px-3">
                    <Lock size={18} />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control border-start-0 border-end-0 py-2.5 px-2 text-dark font-sans shadow-none"
                    placeholder="Digite sua senha"
                    value={password}
                    name="password"
                    id="login-password-input"
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrorMsg(null);
                    }}
                    style={{ backgroundColor: '#ffffff' }}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn bg-white border border-start-0 text-muted px-3 d-flex align-items-center justify-content-center hover:text-dark"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Ocultar senha" : "Exibir senha"}
                    tabIndex={-1}
                    id="btn-toggle-login-password"
                  >
                    {showPassword ? <EyeOff size={18} className="text-primary" /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Multi-Ação Buttons layout */}
              <div className="d-flex flex-column gap-2 mt-2">
                <div className="d-flex align-items-center justify-content-start">
                  <button 
                    type="button" 
                    onClick={() => handleSwitchMode('REQUEST_CODE')}
                    className="btn btn-link text-muted text-xs p-0 text-decoration-none hover:text-dark font-sans text-start fw-semibold"
                    disabled={loading}
                  >
                    Esqueci minha senha / Cadastrar Senha
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary px-4 py-2 fw-semibold d-flex align-items-center gap-2 rounded shadow-sm hover:opacity-90 font-display"
                  style={{ backgroundColor: '#337ab7', borderColor: '#2e6da4', minWidth: '150px' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      <span>Avaliando...</span>
                    </>
                  ) : (
                    <>
                      <span>Fazer login</span>
                      <LogIn size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ==================== VIEW 2: REQUEST OTP CODE ==================== */}
          {viewMode === 'REQUEST_CODE' && (
            <form onSubmit={handleRequestCodeSubmit} className="d-flex flex-column gap-3.5">
              <div className="mb-3 text-start">
                <label className="form-label text-xs font-sans text-muted mb-1.5">Insira seu e-mail cadastrado ou nome de usuário:</label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0 text-muted px-3">
                    <Mail size={18} />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 py-2.5 px-2 text-dark font-sans shadow-none rounded-end"
                    placeholder="Ex: joao.silva@mastercoifa.com.br"
                    value={emailOrUser}
                    onChange={(e) => {
                      setEmailOrUser(e.target.value);
                      setErrorMsg(null);
                    }}
                    style={{ backgroundColor: '#ffffff' }}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <button 
                  type="button" 
                  onClick={() => handleSwitchMode('RESET_PASSWORD')}
                  className="btn btn-link text-muted text-xs p-0 text-decoration-none hover:text-dark font-sans fw-semibold"
                  disabled={loading}
                >
                  Já tenho um código
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4 py-2 fw-semibold d-flex align-items-center gap-2 rounded shadow-sm hover:opacity-90 font-display"
                  style={{ backgroundColor: '#337ab7', borderColor: '#2e6da4' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <span>Solicitar Código</span>
                      <Key size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ==================== VIEW 4: RESET PERMANENT PASSWORD ==================== */}
          {viewMode === 'RESET_PASSWORD' && (
            <form onSubmit={handleResetPasswordSubmit} className="d-flex flex-column gap-3.5">
              <div className="mb-2 text-start">
                <label className="form-label text-xs font-sans text-muted mb-1">Usuário ou E-mail:</label>
                <input
                  type="text"
                  className="form-control py-2 px-3 text-dark font-sans shadow-none rounded"
                  placeholder="E-mail ou nome de usuário"
                  value={emailOrUser}
                  onChange={(e) => setEmailOrUser(e.target.value)}
                  style={{ backgroundColor: '#ffffff' }}
                  required
                  disabled={loading}
                />
              </div>

              <div className="mb-2 text-start mt-2">
                <label className="form-label text-xs font-sans text-muted mb-1">Código de Segurança (6 dígitos):</label>
                <input
                  type="text"
                  maxLength={6}
                  className="form-control py-2 px-3 text-dark font-sans tracking-wider fw-bold text-center shadow-none rounded"
                  placeholder="Digite o código enviado"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  style={{ backgroundColor: '#ffffff' }}
                  required
                  disabled={loading}
                />
              </div>

              <div className="mb-2 text-start mt-2">
                <label className="form-label text-xs font-sans text-muted mb-1">Nova Senha Corporativa:</label>
                <div className="input-group">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control py-2 px-3 text-dark font-sans shadow-none border-end-0 rounded-start"
                    placeholder="Mínimo 4 caracteres"
                    value={password}
                    id="new-password-input"
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ backgroundColor: '#ffffff' }}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn bg-white border border-start-0 text-muted px-3 d-flex align-items-center justify-content-center hover:text-dark rounded-end"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    title={showNewPassword ? "Ocultar senha" : "Exibir senha"}
                    tabIndex={-1}
                    id="btn-toggle-new-password"
                  >
                    {showNewPassword ? <EyeOff size={18} className="text-primary" /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="mb-2 text-start mt-2">
                <label className="form-label text-xs font-sans text-muted mb-1">Confirmar Nova Senha:</label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control py-2 px-3 text-dark font-sans shadow-none border-end-0 rounded-start"
                    placeholder="Digite novamente"
                    value={confirmPassword}
                    id="confirm-new-password-input"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ backgroundColor: '#ffffff' }}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn bg-white border border-start-0 text-muted px-3 d-flex align-items-center justify-content-center hover:text-dark rounded-end"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    title={showConfirmPassword ? "Ocultar senha" : "Exibir senha"}
                    tabIndex={-1}
                    id="btn-toggle-confirm-password"
                  >
                    {showConfirmPassword ? <EyeOff size={18} className="text-primary" /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary px-4 py-2 fw-semibold d-flex align-items-center gap-2 rounded shadow-sm hover:opacity-90 font-display"
                  style={{ backgroundColor: '#337ab7', borderColor: '#2e6da4' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <span>Redefinir e Salvar</span>
                      <CheckCircle size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
